import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, Target, Share2, Bookmark } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import SEOHead from '@/components/SEO/SEOHead';
import StructuredData, { createHowToSchema } from '@/components/SEO/StructuredData';
import { getGuideBySlug, getPublishedGuides } from '@/services/cms';

const GuidePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [guide, setGuide] = useState(null);
  const [relatedGuides, setRelatedGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [readingTime, setReadingTime] = useState(0);

  useEffect(() => {
    const fetchGuide = async () => {
      try {
        setLoading(true);
        const data = await getGuideBySlug(slug);
        
        if (!data) {
          setError('Guide not found');
          return;
        }
        
        setGuide(data);
        
        // Calculate reading time
        const wordCount = data.content.split(/\s+/).length;
        setReadingTime(Math.ceil(wordCount / 200));
        
        // Fetch related guides
        const allGuides = await getPublishedGuides();
        const related = allGuides
          .filter(g => g.id !== data.id && g.guideType === data.guideType)
          .slice(0, 3);
        setRelatedGuides(related);
      } catch (err) {
        console.error('Error loading guide:', err);
        setError('Failed to load guide');
      } finally {
        setLoading(false);
      }
    };

    fetchGuide();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  if (error || !guide) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Guide Not Found</h1>
          <Button onClick={() => navigate('/guides')}>
            <ArrowLeft className="mr-2 w-4 h-4" />
            Back to Guides
          </Button>
        </div>
      </div>
    );
  }

  const seoTitle = guide.seoMetadata?.metaTitle || guide.title;
  const seoDescription = guide.seoMetadata?.metaDescription || guide.description;
  const seoKeywords = guide.seoMetadata?.keywords || [];

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        ogImage={guide.featuredImage}
        ogType="article"
        canonicalUrl={`/guides/${guide.slug}`}
      />
      
      <StructuredData data={createHowToSchema(guide)} />

      <div className="min-h-screen bg-white">
        {/* Breadcrumb - Sticky Header */}
        <div className="sticky top-16 z-40 bg-gray-50 border-b border-gray-200 shadow-sm backdrop-blur-sm bg-opacity-95">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <button onClick={() => navigate('/')} className="hover:text-cyan-600 transition-colors">Home</button>
              <span>/</span>
              <button onClick={() => navigate('/guides')} className="hover:text-cyan-600 transition-colors">Guides</button>
              <span>/</span>
              <span className="text-gray-900 truncate">{guide.title}</span>
            </div>
          </div>
        </div>

        {/* Hero Section */}
        <div className="max-w-5xl mx-auto px-4 pt-8 pb-6">
          {/* Badges */}
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <span className="inline-block bg-cyan-100 text-cyan-700 px-4 py-1.5 rounded-full text-sm font-semibold">
              {guide.guideType}
            </span>
            {guide.level && (
              <Badge variant="outline" className="border-cyan-600 text-cyan-700">
                <Target className="w-3 h-3 mr-1" />
                {guide.level}
              </Badge>
            )}
            {guide.duration && (
              <Badge variant="outline" className="border-cyan-600 text-cyan-700">
                <Clock className="w-3 h-3 mr-1" />
                {guide.duration}
              </Badge>
            )}
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight font-sans">
            {guide.title}
          </h1>

          {/* Description */}
          <p className="text-xl text-gray-600 mb-8 leading-relaxed">
            {guide.description}
          </p>

          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-6 text-gray-600 mb-8">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              <span>{readingTime} min read</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 mb-8">
            <Button variant="outline" size="sm" className="gap-2">
              <Share2 className="w-4 h-4" />
              Share
            </Button>
            <Button variant="outline" size="sm" className="gap-2">
              <Bookmark className="w-4 h-4" />
              Save
            </Button>
          </div>

          {/* Featured Image */}
          {guide.featuredImage && (
            <div className="mb-12 rounded-2xl overflow-hidden shadow-2xl">
              <img
                src={guide.featuredImage}
                alt={guide.title}
                className="w-full h-auto object-cover"
              />
            </div>
          )}
        </div>

        {/* Content Area */}
        <div className="max-w-5xl mx-auto px-4 pb-16">
          {/* Main Content with same styling as BlogPost */}
          <div className="blog-content" dangerouslySetInnerHTML={{ __html: guide.content }}>
          </div>

          {/* CTA Section */}
          <Card className="my-16 bg-gradient-to-r from-cyan-600 to-turquoise-600 text-white p-10 text-center rounded-2xl shadow-xl">
            <h3 className="text-3xl font-bold mb-4">Ready to Create Stunning Designs?</h3>
            <p className="text-xl mb-6 text-cyan-50">
              Transform your ideas into professional T-shirt designs in seconds with Fluxara's AI-powered tools.
            </p>
            <Button 
              size="lg" 
              onClick={() => navigate('/generate')}
              className="bg-white text-cyan-600 hover:bg-gray-100 font-semibold px-8 py-6 text-lg"
            >
              Start Designing Now
            </Button>
          </Card>

          {/* Related Guides */}
          {relatedGuides.length > 0 && (
            <div className="mt-20">
              <h2 className="text-3xl font-bold mb-8 text-gray-900">Related Guides</h2>
              <div className="grid md:grid-cols-3 gap-6">
                {relatedGuides.map((relatedGuide) => (
                  <Card
                    key={relatedGuide.id}
                    className="cursor-pointer hover:shadow-xl transition-all duration-300 overflow-hidden group"
                    onClick={() => {
                      window.scrollTo(0, 0);
                      navigate(`/guides/${relatedGuide.slug}`);
                    }}
                  >
                    {relatedGuide.featuredImage && (
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={relatedGuide.featuredImage}
                          alt={relatedGuide.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                        <div className="absolute top-4 left-4">
                          <span className="bg-cyan-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                            {relatedGuide.guideType}
                          </span>
                        </div>
                      </div>
                    )}
                    <div className="p-6">
                      <h3 className="text-xl font-bold mb-2 text-gray-900 group-hover:text-cyan-600 transition-colors line-clamp-2">
                        {relatedGuide.title}
                      </h3>
                      <p className="text-gray-600 text-sm line-clamp-2 mb-4">{relatedGuide.description}</p>
                      <div className="flex items-center text-sm text-gray-500">
                        <Clock className="w-4 h-4 mr-1" />
                        <span>{relatedGuide.duration || '5 min read'}</span>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Back to Guides Button */}
          <div className="mt-16 text-center">
            <Button 
              variant="outline" 
              size="lg"
              onClick={() => navigate('/guides')}
              className="gap-2"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to All Guides
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default GuidePage;